// src/pages/payments/TelegramConnect.jsx
// FASE 3.A — PG-06 Conectar Telegram (pixel-perfect del mockup v4).
// FASE 3.B — el código EE-XXXX-XXXX se obtiene desde la callable
// requestTelegramBindingCode (TTL 10 min, ligado al uid en Firestore).
// FASE 3.C — onSnapshot al doc /telegramBindings/{code} para detectar cuando
// el bot Telegram (vía CF telegramBindCode) marca used:true. Al detectarlo,
// redirige al dashboard con un pequeño delay para feedback visual.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { functions, firestore } from '../../firebase';
import EELogo from '../../components/EELogo';
import { useAuth } from '../../auth/AuthContext';
import { useLogoutFlow } from '../../auth/useLogoutFlow';
import { useSmartBack } from '../../auth/useSmartBack';
import SplashScreen from '../../components/SplashScreen';

const BOT_USERNAME = 'EdgeEliteBot';

// Determina qué pantalla mostrar según el estado del user. La CF
// requestTelegramBindingCode rechaza si no hay accessUntil vigente
// y eso muestra un error genérico al user. Pre-clasificamos acá:
//   admin       → es admin del producto, no necesita Telegram.
//   already     → ya tiene telegramChatId, vinculación previa.
//   no-access   → sin pago ni beta vigente, redirigir a paywall.
//   bind        → flujo normal de vinculación.
//
// Recibimos hasAccess del contexto (computeHasAccess en AuthContext) que
// ya considera betaAccess + accessUntil + pagoActivo legacy. NO duplicar
// la lógica acá: si AuthContext gana una rama nueva (ej. trial,
// invitaciones), este componente se beneficia automáticamente.
function classifyScreen(userData, isAdmin, hasAccess) {
  if (isAdmin) return 'admin';
  if (userData && userData.telegramChatId) return 'already';
  if (!hasAccess) return 'no-access';
  return 'bind';
}

export default function TelegramConnect() {
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const { userData, isAdmin, hasAccess } = useAuth();
  const { showLogoutSplash, startLogout, handleLogoutComplete } = useLogoutFlow();
  const [code, setCode] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [linked, setLinked] = useState(false);

  const screen = classifyScreen(userData, isAdmin, hasAccess);

  // FIX bug crítico (paywall-trapped-user): escape del flujo de vinculación.
  // FASE UX: useLogoutFlow encapsula el flow completo con splash.
  const handleSignOut = startLogout;

  useEffect(() => {
    // Solo pedir el código en el flujo normal de bind. Para admin /
    // already / no-access mostramos pantalla informativa sin llamar
    // a la CF (que rechazaría con failed-precondition).
    if (screen !== 'bind') return;
    let cancelled = false;
    (async () => {
      try {
        const callable = httpsCallable(functions, 'requestTelegramBindingCode');
        const result = await callable();
        if (cancelled) return;
        const c = result.data && result.data.code;
        if (!c) throw new Error('Sin código en respuesta.');
        setCode(c);
      } catch (err) {
        if (cancelled) return;
        console.error('[TelegramConnect] error generando código:', err);
        setError('No pudimos generar tu código. Refresca la página.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [screen]);

  // FASE 3.C — escuchar el doc del binding. Cuando bot setea used:true,
  // mostrar feedback "¡Vinculado!" por 1.5s y redirigir al dashboard.
  useEffect(() => {
    if (!code) return;
    const ref = doc(firestore, 'telegramBindings', code);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data();
        if (data && data.used === true) {
          setLinked(true);
          setTimeout(() => navigate('/dashboard'), 1500);
        }
      },
      (err) => {
        console.error('[TelegramConnect] binding snapshot error:', err);
      }
    );
    return () => unsub();
  }, [code, navigate]);

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard API not available — silently ignore */
    }
  };

  const botDeepLink = code
    ? `https://t.me/${BOT_USERNAME}?start=${encodeURIComponent(code)}`
    : `https://t.me/${BOT_USERNAME}`;
  // Copiamos `/start <code>` al portapapeles antes de abrir el deep link.
  // Si el deep link tarda en responder (polling del bot puede demorar
  // unos segundos), el user puede pegar manualmente como fallback rápido.
  const openBot = async () => {
    if (code) {
      try {
        await navigator.clipboard.writeText(`/start ${code}`);
      } catch (err) {
        console.warn('[TelegramConnect] clipboard copy failed:', err);
      }
    }
    window.open(botDeepLink, '_blank', 'noopener,noreferrer');
  };

  // FIX BUG 2: early return cuando hay logout splash activo.
  if (showLogoutSplash) {
    return <SplashScreen type="logout" onComplete={handleLogoutComplete} />;
  }

  // ── Pantallas alternativas para los escenarios donde NO aplica
  // el flujo normal de vinculación. Evitan llamar a la CF
  // requestTelegramBindingCode que rechaza con error genérico.

  if (screen === 'admin') {
    return (
      <InfoScreen
        title="Modo admin"
        emoji="✅"
        description="Tienes acceso completo al dashboard sin necesidad de Telegram."
        ctaLabel="Ir al Dashboard"
        ctaOnClick={() => navigate('/dashboard')}
        goBack={goBack}
        handleSignOut={handleSignOut}
      />
    );
  }

  if (screen === 'already') {
    return (
      <InfoScreen
        title="Ya tienes Telegram conectado"
        emoji="✅"
        description={
          <>
            Las alertas llegan al grupo VIP donde estás unido.
            <br />
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>
              ¿Perdiste el grupo? Ve a Telegram y escribe <code>/grupo</code> a{' '}
              <b>@{BOT_USERNAME}</b>.
            </span>
          </>
        }
        ctaLabel="Ir al Dashboard"
        ctaOnClick={() => navigate('/dashboard')}
        goBack={goBack}
        handleSignOut={handleSignOut}
      />
    );
  }

  if (screen === 'no-access') {
    return (
      <InfoScreen
        title="Tu acceso venció"
        emoji="⚠️"
        description="Renueva tu plan para volver a recibir alertas."
        ctaLabel="Renovar plan →"
        ctaOnClick={() => navigate('/paywall')}
        goBack={goBack}
        handleSignOut={handleSignOut}
      />
    );
  }

  return (
    <div className="mobile-root">
      <Helmet>
        <title>Conectar Telegram · Edge Elite</title>
      </Helmet>

      <section className="screen" style={{ paddingTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <button
            onClick={goBack}
            aria-label="Volver"
            title="Volver"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'var(--bg-elev)',
              border: '1px solid var(--line)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <EELogo size={18} />
          {/* FIX bug crítico: botón Cerrar sesión visible */}
          <button
            type="button"
            onClick={handleSignOut}
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
            style={{
              marginLeft: 'auto',
              padding: '8px 12px',
              borderRadius: 10,
              background: 'transparent',
              border: '1px solid var(--line)',
              color: 'var(--muted)',
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'inherit',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Cerrar sesión
          </button>
        </div>

        <div
          style={{
            marginTop: 24,
            padding: '32px 20px',
            background: 'linear-gradient(135deg, #229ED9, #1A7CB8)',
            borderRadius: 20,
            textAlign: 'center',
            color: '#fff',
          }}
        >
          <div
            style={{
              width: 68,
              height: 68,
              borderRadius: 999,
              background: 'rgba(255,255,255,.15)',
              margin: '0 auto 18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="#fff">
              <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
            </svg>
          </div>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 700,
              margin: '0 0 8px',
              letterSpacing: '-0.02em',
            }}
          >
            Conecta tu Telegram
          </h2>
          <p style={{ fontSize: 14, opacity: 0.85, margin: 0, lineHeight: 1.5 }}>
            Te uniremos al grupo de alertas en vivo y a la comunidad VIP donde se comentan las apuestas.
          </p>
        </div>

        <div style={{ marginTop: 24 }}>
          {/* Step 1 — Vincula tu cuenta (con botón Abrir Telegram + fallback código) */}
          <div
            style={{
              background: 'var(--bg-elev)',
              border: '1px solid var(--line)',
              borderRadius: 14,
              padding: 18,
              display: 'flex',
              gap: 14,
              alignItems: 'flex-start',
              marginBottom: 10,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: 'var(--bg-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              1
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>Vincula tu cuenta</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 3, lineHeight: 1.45 }}>
                Toca el botón. Telegram se abre con todo precargado, solo confirma.
              </div>
              {/* F5 hotfix 2026-05-10: deshabilitar mientras code es null
                  para evitar que el deep link caiga al fallback sin
                  ?start=CODE. Si user click antes que requestTelegramBindingCode
                  responda, openBot() abre t.me/EdgeEliteBot sin parámetro,
                  Telegram manda /start plano y bot ignora el bind. */}
              <button
                onClick={openBot}
                disabled={!code}
                style={{
                  marginTop: 12,
                  width: '100%',
                  padding: '12px 16px',
                  background: code ? '#229ED9' : 'var(--bg-elev)',
                  color: code ? '#fff' : 'var(--muted)',
                  border: code ? 0 : '1px solid var(--line)',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: code ? 'pointer' : 'not-allowed',
                  opacity: code ? 1 : 0.7,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  fontFamily: 'inherit',
                }}
              >
                {code ? (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
                    </svg>
                    Abrir Telegram
                  </>
                ) : (
                  'Generando código…'
                )}
              </button>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--muted)',
                  marginTop: 10,
                  lineHeight: 1.5,
                }}
              >
                Te llevaremos al chat del bot. Espera unos segundos mientras te respondemos.
                Si no pasa nada en 10 segundos, pega <code style={{
                  background: 'var(--bg-subtle)',
                  padding: '1px 5px',
                  borderRadius: 4,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                }}>/start [tu-código]</code> (ya lo copiamos al portapapeles).
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--muted)',
                  marginTop: 14,
                  marginBottom: 6,
                }}
              >
                Si Telegram te pide el código, copia este:
              </div>
              <div
                className="num"
                style={{
                  marginTop: 0,
                  padding: '10px 14px',
                  background: linked ? 'rgba(22,163,74,.12)' : 'var(--bg-subtle)',
                  borderRadius: 10,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 15,
                  fontWeight: 600,
                  letterSpacing: '.08em',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: linked
                    ? 'var(--v-enter, #16a34a)'
                    : error
                    ? 'var(--danger, #d33)'
                    : 'inherit',
                }}
              >
                <span>
                  {linked
                    ? '✓ ¡Vinculado! Llevándote al dashboard…'
                    : error
                    ? error
                    : code
                    ? code
                    : 'Generando código…'}
                </span>
                {code && !error && !linked && (
                  <button
                    onClick={handleCopy}
                    style={{
                      background: 0,
                      border: 0,
                      color: 'var(--accent)',
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    {copied ? 'COPIADO' : 'COPIAR'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Step 2 — Listo 🎉 (expectativa correcta: link al grupo VIP llega en el chat del bot) */}
          <div
            style={{
              background: 'var(--bg-elev)',
              border: '1px solid var(--line)',
              borderRadius: 14,
              padding: 18,
              display: 'flex',
              gap: 14,
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: 'var(--bg-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              2
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>El bot te suma a 2 grupos 🎉</div>
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 14, flexShrink: 0, lineHeight: 1.3 }}>🔔</span>
                  <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                    <strong>Grupo de alertas</strong>{' '}
                    <span style={{ color: 'var(--muted)' }}>— recibes el análisis en vivo</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 14, flexShrink: 0, lineHeight: 1.3 }}>👥</span>
                  <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                    <strong>Comunidad VIP</strong>{' '}
                    <span style={{ color: 'var(--muted)' }}>— comenta apuestas y aprende con otros</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card "Ingresa a tu Dashboard" */}
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            width: '100%',
            marginTop: 14,
            padding: 16,
            background: 'var(--accent-soft)',
            border: '1.5px solid var(--accent)',
            borderRadius: 14,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            textAlign: 'left',
            cursor: 'pointer',
            fontFamily: 'inherit',
            boxShadow: '0 4px 16px rgba(255,107,44,.12)',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="3" width="20" height="13" rx="1.8" stroke="#fff" strokeWidth="1.6" fill="rgba(255,255,255,.1)" />
              <path d="M9 20h6M12 16v4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
              <circle cx="12" cy="9.5" r="3.2" fill="#FF9F45" stroke="#fff" strokeWidth="1.2" />
              <path
                d="M12 6.3v6.4M8.8 9.5h6.4M9.9 7.1c.6 1.5.6 3.3 0 4.8M14.1 7.1c-.6 1.5-.6 3.3 0 4.8"
                stroke="#8B2500"
                strokeWidth=".8"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Tu Dashboard — lo fundamental</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 3, lineHeight: 1.45 }}>
              Analiza partidos en vivo, revisa tu historial con win rate y ROI, y filtra por liga.
            </div>
          </div>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--accent)"
            strokeWidth="2.4"
            style={{ flexShrink: 0 }}
          >
            <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <button className="btn-secondary" style={{ marginTop: 22 }} onClick={() => navigate('/dashboard')}>
          Ya estoy vinculado
        </button>
      </section>
    </div>
  );
}


// Pantalla informativa simple — usada para los 3 escenarios alternativos
// (admin, ya vinculado, sin acceso). Reusa el header (back + Cerrar
// sesión) del flujo de bind para coherencia visual.
function InfoScreen({ title, emoji, description, ctaLabel, ctaOnClick, goBack, handleSignOut }) {
  return (
    <div className="mobile-root">
      <Helmet>
        <title>{title} · Edge Elite</title>
      </Helmet>
      <section className="screen" style={{ paddingTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <button
            onClick={goBack}
            aria-label="Volver"
            title="Volver"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'var(--bg-elev)',
              border: '1px solid var(--line)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <EELogo size={18} />
          <button
            type="button"
            onClick={handleSignOut}
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
            style={{
              marginLeft: 'auto',
              padding: '8px 12px',
              borderRadius: 10,
              background: 'transparent',
              border: '1px solid var(--line)',
              color: 'var(--muted)',
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'inherit',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Cerrar sesión
          </button>
        </div>

        <div
          style={{
            marginTop: 60,
            padding: '40px 24px',
            background: 'var(--bg-elev)',
            border: '1px solid var(--line)',
            borderRadius: 20,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 56, marginBottom: 18 }}>{emoji}</div>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 700,
              margin: '0 0 12px',
              letterSpacing: '-0.02em',
            }}
          >
            {title}
          </h2>
          <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0, lineHeight: 1.55 }}>
            {description}
          </p>
        </div>

        <button
          className="btn-primary accent"
          style={{ marginTop: 24 }}
          onClick={ctaOnClick}
        >
          {ctaLabel}
        </button>
      </section>
    </div>
  );
}
